(() => {
  if (window.__eamdCartExtrasLoaded) return;
  window.__eamdCartExtrasLoaded = true;

  const FALLBACKS = {
    'cart.recommendationsTitle': 'Los más regalados',
    'cart.completeYourPurchaseTitle': 'Completa tu compra',
    'cart.viewDetails': 'Ver detalles',
    'cart.paymentMethods': 'Medios de pago',
    'cart.freeShippingComplete': '¡Listo! Tu envío es gratis.',
    'cart.freeShippingRemaining': 'Te faltan {amount} para el envío gratis.',
    'cart.freeShippingProgress': 'Progreso de envío gratis',
    'cart.remove': 'Eliminar',
    'cart.decreaseQuantity': 'Disminuir cantidad',
    'cart.increaseQuantity': 'Aumentar cantidad',
    'cart.checkout': 'PAGA DE FORMA SEGURA',
    'accessibility.close': 'Cerrar'
  };

  const runtimeConfig = () => window.__EAMD_CONFIG__ || {};
  const readPath = (object, path) => path.split('.').reduce((value, key) => value?.[key], object);
  const snakePath = (path) => path
    .split('.')
    .map((part) => part.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`))
    .join('.');

  const translate = (path, fallback = FALLBACKS[path] || '') => {
    const dictionary = runtimeConfig().i18n || {};
    const value = readPath(dictionary, path) ?? readPath(dictionary, snakePath(path));
    return typeof value === 'string' && value.trim() ? value : fallback;
  };

  const interpolate = (template, values = {}) => Object.entries(values).reduce(
    (copy, [key, value]) => copy.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value)),
    String(template || '')
  );

  const config = () => {
    const node = document.querySelector('[data-eamd-cart-extras]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_) {
      return null;
    }
  };

  const normalizedRootUrl = () => {
    const global = runtimeConfig();
    const configured = global.rootUrl || global.routes?.root || global.routes?.rootUrl || '/';
    const pathname = new URL(configured, window.location.origin).pathname;
    return pathname === '/' ? '/' : `${pathname.replace(/\/+$/, '')}/`;
  };

  const localizeInternalUrl = (href) => {
    if (!href || /^(#|mailto:|tel:|sms:|javascript:|data:)/i.test(href)) return href;
    let parsed;
    try {
      parsed = new URL(href, window.location.origin);
    } catch (_) {
      return href;
    }
    if (parsed.origin !== window.location.origin) return href;
    const root = normalizedRootUrl();
    const rootPath = root === '/' ? '' : root.replace(/\/$/, '');
    if (rootPath && parsed.pathname !== rootPath && !parsed.pathname.startsWith(`${rootPath}/`)) {
      parsed.pathname = `${rootPath}/${parsed.pathname.replace(/^\/+/, '')}`;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  };

  const route = (keys, fallbackPath) => {
    const global = runtimeConfig();
    const routes = global.routes || {};
    const names = Array.isArray(keys) ? keys : [keys];
    for (const key of names) {
      const value = routes[key] ?? global[key];
      if (typeof value === 'string' && value) return localizeInternalUrl(value);
    }
    return localizeInternalUrl(fallbackPath);
  };

  // Shopify cart Ajax prices are integer minor currency units.
  const money = (cents) => {
    const global = runtimeConfig();
    // Idioma Y país («es-CO»): con «es» a secas el símbolo sale detrás («63.000 $»).
    const base = global.localeCode || document.documentElement.lang || navigator.language;
    const locale = global.countryCode && !String(base).includes('-') ? `${base}-${global.countryCode}` : base;
    const currency = global.currencyCode || window.Shopify?.currency?.active;
    const value = Number(cents || 0) / 100;
    const hasFraction = Math.abs(value - Math.round(value)) > Number.EPSILON;
    if (!currency) return new Intl.NumberFormat(locale).format(value);
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2
      }).format(value);
    } catch (_) {
      return `${new Intl.NumberFormat(locale).format(value)} ${currency}`.trim();
    }
  };

  // The React island owns the drawer DOM. Locate it by its stable mount and fixed
  // right-rail structure, never by locale-dependent visible copy.
  const findDrawer = () => {
    const root = document.querySelector('[data-eamd-cart-root], [data-component="CartDrawerIsland"]');
    if (!root) return null;
    return Array.from(root.children).find((node) => (
      node instanceof HTMLElement
      && node.tagName === 'DIV'
      && node.classList.contains('fixed')
      && node.classList.contains('right-0')
    )) || null;
  };

  const cardMarkup = (product) => {
    const link = document.createElement('a');
    link.className = 'eamd-cart-rec__card';
    link.href = localizeInternalUrl(product.url);
    link.dataset.ddCta = 'cart-empty-recommendation';
    if (product.image) {
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = product.title || '';
      img.width = 72;
      img.height = 72;
      img.loading = 'lazy';
      link.append(img);
    }
    const copy = document.createElement('span');
    copy.className = 'eamd-cart-rec__copy';
    const title = document.createElement('span');
    title.className = 'eamd-cart-rec__title';
    title.textContent = product.title;
    copy.append(title);
    // Ratings only when the product really has them; nothing invented.
    if (product.rating && product.ratingCount) {
      const rating = document.createElement('span');
      rating.className = 'eamd-cart-rec__rating';
      rating.textContent = `★ ${product.rating} · ${product.ratingCount}`;
      copy.append(rating);
    }
    const price = document.createElement('span');
    price.className = 'eamd-cart-rec__price';
    // `product.price` ya llega formateado (normalizarDelPool/normalizarDeLaApi corren
    // `money()` antes de que esta funcion se llame), a diferencia de los centavos crudos que
    // sirve Liquid.
    price.textContent = product.price;
    copy.append(price);
    link.append(copy);
    return link;
  };

  const mountRecommendations = (drawer, { title, productos }) => {
    if (!productos.length) return;
    drawer.querySelector('[data-eamd-cart-rec]')?.remove();

    const block = document.createElement('section');
    block.className = 'eamd-cart-rec';
    block.dataset.eamdCartRec = 'true';
    block.dataset.ddComponent = 'EamdCartRecommendations';
    block.dataset.ddTrack = 'component_view component_click';

    const heading = document.createElement('p');
    heading.className = 'eamd-cart-rec__eyebrow';
    heading.textContent = title;
    block.append(heading);

    const isla = document.createElement('div');
    isla.className = 'react-island';
    isla.dataset.component = 'CartUpsellCarousel';
    isla.setAttribute('data-props', JSON.stringify({ title, products: productos }));
    block.append(isla);
    drawer.append(block);

    // 🔴 El cajon se inyecta DESPUES de que el montador hizo su pasada, asi que hay que
    // pedirle que reclame este nodo. Sin esto la isla se queda con su clase puesta y no la
    // monta nadie: ni nosotros ni el bundle heredado, y sin un solo error en consola.
    if (window.__eamdIslas?.montar) {
      window.__eamdIslas.montar(block);
      // Si la isla monto, se retira el titulo servido: lo vuelve a pintar ella.
      if (!isla.classList.contains('react-island')) heading.remove();
      return;
    }

    // RESPALDO: si el bundle de islas no cargo, el comprador NO se queda sin recomendados.
    // Vuelve la lista apilada de siempre (solo enlaza, sin decidir variante), que es peor
    // pero existe.
    isla.remove();
    const list = document.createElement('div');
    list.className = 'eamd-cart-rec__list';
    productos.slice(0, 4).forEach((product) => list.append(cardMarkup(product)));
    block.append(list);
  };

  const removeRecommendations = (drawer) => {
    drawer.querySelector('[data-eamd-cart-rec]')?.remove();
  };

  /**
   * ── De dónde salen los recomendados en cada estado ───────────────────────────
   *
   * VACÍO: el pool de reserva que Liquid ya sirvió (`settings.recommendations`) — la API de
   * Shopify se ancla a un `product_id` y un carrito vacío no tiene ninguno
   * (`cart-drawer-upsells-standard.md`).
   *
   * LLENO: `/recommendations/products.js` con el `product_id` de la PRIMERA línea e
   * `intent=related` (el que trae datos sin configuración de admin — `intent=complementary`
   * viene vacío en la mayoría de tiendas, medido en la convención). Si la API falla o
   * devuelve 0, cae al MISMO pool de reserva — nunca un cajón sin nada que ofrecer.
   *
   * En los dos casos: fuera cualquier producto que YA esté en el carrito (dedupe por
   * `product_id`), y cada producto lleva `variantId` (una sola variante comprable → se puede
   * añadir directo) o `needsDecision: true` (navega a la PDP; el quickview drawer es #127).
   */
  const normalizarDelPool = (product) => ({
    productId: product.productId ?? null,
    url: localizeInternalUrl(product.url),
    image: product.image || null,
    title: product.title,
    price: money(product.price),
    rating: product.rating || null,
    ratingCount: product.ratingCount || null,
    variantId: product.variantId ?? null,
    needsDecision: Boolean(product.needsDecision)
  });

  const normalizarDeLaApi = (product) => {
    const variantesComprables = (product.variants || []).filter((v) => v.available);
    const unaSolaVariante = product.variants?.length === 1 && variantesComprables.length === 1;
    return {
      productId: product.id,
      url: localizeInternalUrl(product.url),
      image: product.featured_image?.url || product.images?.[0] || null,
      title: product.title,
      price: money(product.price),
      // La respuesta de `/recommendations/products.js` no trae metaobjetos: sin rating aqui,
      // nunca uno inventado.
      rating: null,
      ratingCount: null,
      variantId: unaSolaVariante ? variantesComprables[0].id : null,
      needsDecision: (product.variants?.length || 0) > 1
    };
  };

  const RECOMENDACIONES_LIMITE = 8;

  const excluyendoElCarrito = (productos, idsEnElCarrito) => (
    productos.filter((p) => !p.productId || !idsEnElCarrito.has(p.productId))
  );

  const recomendadosDelPool = (settings, idsEnElCarrito) => excluyendoElCarrito(
    (settings.recommendations || []).map(normalizarDelPool),
    idsEnElCarrito
  ).slice(0, RECOMENDACIONES_LIMITE);

  const recomendadosDeLaApi = async (productId, idsEnElCarrito) => {
    try {
      const url = route(['recommendationsUrl'], '/recommendations/products.js');
      const res = await fetch(`${url}?product_id=${encodeURIComponent(productId)}&intent=related&limit=${RECOMENDACIONES_LIMITE}`, {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return [];
      const data = await res.json();
      return excluyendoElCarrito((data.products || []).map(normalizarDeLaApi), idsEnElCarrito);
    } catch (_) {
      return [];
    }
  };

  /**
   * 🔴 La barra de envío gratis de este archivo ESTABA DUPLICADA y se ha retirado.
   *
   * `mountShippingBar` hacía `drawer.prepend(...)` con `[data-eamd-ship-bar]`, o sea que se
   * colocaba arriba del todo del cajón. Pero el cajón ya trae su PROPIO progreso de envío
   * —lo declara su raíz, `data-eamd-shipping-progress-label="Progreso para obtener envío
   * gratis"`— justo debajo del título. El comprador veía la misma promesa dos veces, con dos
   * redacciones y dos formatos de moneda («15.000 $» arriba, «15.000 COP» abajo).
   *
   * Se quita la de arriba, que es la nuestra y la que sobra: la de abajo va con el subtotal y
   * el botón de pagar, que es donde el dato sirve para decidir.
   *
   * Sólo se retira la barra. Los recomendados de este archivo siguen.
   */
  const removeShippingBar = (drawer) => {
    // Se sigue barriendo: un cajón que ya tuviera la barra pintada de una visita anterior debe
    // perderla, no quedársela hasta que alguien recargue.
    drawer.querySelector('[data-eamd-ship-bar]')?.remove();
  };

  const localizeLinks = (scope) => {
    scope?.querySelectorAll('a[href]').forEach((link) => {
      const localized = localizeInternalUrl(link.getAttribute('href'));
      if (localized && localized !== link.getAttribute('href')) link.setAttribute('href', localized);
    });
  };

  /**
   * Las propiedades de una línea (Inicial 1, Piedra 2…) van PLEGADAS tras «Ver detalles».
   *
   * El cajón las pintaba todas, un párrafo por propiedad: una pulsera de dos iniciales y dos
   * piedras ocupaba seis líneas de texto gris antes de la cantidad (Max, 2026-09-23). La línea
   * de variante («Dorado / tejido 1 / Enero») se queda a la vista: es el resumen.
   *
   * 🔴 El cajón es React COMPILADO: sus nodos no se mueven, React los reconcilia y un nodo
   * movido acaba duplicado o reventando el render. Se OCULTAN (`data-eamd-prop`) y al lado se
   * pone un `<details>` nativo propio. La firma de las propiedades decide si hay que rehacerlo:
   * si el comprador cambia algo, el bloque se reconstruye; si no, no se toca.
   */
  const plegarDetalles = (row) => {
    const textos = row.querySelector('.flex.items-start.justify-between.gap-2 > div');
    if (!textos) return;
    // [0] título, [1] variante, [2…] propiedades «Clave: valor».
    const props = Array.from(textos.querySelectorAll(':scope > p')).slice(2)
      .map((p) => ({ p, m: (p.textContent || '').match(/^([^:]{1,40}):\s*(.+)$/) }))
      .filter((x) => x.m);
    const viejo = textos.querySelector(':scope > details[data-eamd-detalles]');
    if (!props.length) { viejo?.remove(); return; }

    // Se marcan SIEMPRE, antes de mirar la firma: si React recrea los párrafos con el mismo
    // texto, la firma no cambia y, sin esto, volverían a verse junto al plegable.
    props.forEach(({ p }) => { p.dataset.eamdProp = 'true'; });
    const firma = props.map((x) => x.p.textContent).join('|');
    if (viejo?.dataset.firma === firma) return;
    viejo?.remove();

    const detalles = document.createElement('details');
    detalles.className = 'eamd-cart-detalles';
    detalles.dataset.eamdDetalles = 'true';
    detalles.dataset.firma = firma;
    detalles.dataset.ddCta = 'cart-line-details';
    const resumen = document.createElement('summary');
    resumen.textContent = `${translate('cart.viewDetails')} (${props.length})`;
    const tabla = document.createElement('dl');
    props.forEach(({ m }) => {
      const dt = document.createElement('dt');
      dt.textContent = m[1].trim();
      const dd = document.createElement('dd');
      dd.textContent = m[2].trim();
      tabla.append(dt, dd);
    });
    detalles.append(resumen, tabla);
    textos.append(detalles);
  };

  const localizeCartChrome = (drawer) => {
    const root = document.querySelector('[data-eamd-cart-root], [data-component="CartDrawerIsland"]');
    if (!root || !drawer) return;
    localizeLinks(drawer);

    const header = Array.from(drawer.children).find((node) => node.querySelector?.('h2'));
    const close = header?.querySelector('button');
    const closeLabel = root.dataset.eamdCloseLabel || translate('accessibility.close');
    if (close && closeLabel && close.getAttribute('aria-label') !== closeLabel) close.setAttribute('aria-label', closeLabel);

    const progress = drawer.querySelector('[role="progressbar"]:not([data-eamd-ship-track])');
    const progressRegion = progress?.parentElement?.parentElement;
    const progressHeading = progressRegion?.querySelector('span');
    const progressLabel = root.dataset.eamdShippingProgressLabel || translate('cart.freeShippingProgress');
    if (progressHeading && progressLabel && progressHeading.textContent !== progressLabel) {
      progressHeading.textContent = progressLabel;
    }

    const productRows = new Set();
    drawer.querySelectorAll('img[alt]').forEach((image) => {
      const row = image.closest('.flex.gap-4.items-start');
      if (row) productRows.add(row);
    });
    productRows.forEach((row) => {
      plegarDetalles(row);
      const productTitle = row.querySelector('img[alt]')?.getAttribute('alt') || '';
      const itemHeader = row.querySelector('.flex.items-start.justify-between.gap-2');
      const remove = itemHeader?.querySelector(':scope > button');
      const removeCopy = root.dataset.eamdRemoveLabel || translate('cart.remove');
      if (remove && removeCopy) remove.setAttribute('aria-label', productTitle ? `${removeCopy}: ${productTitle}` : removeCopy);

      const quantityGroup = Array.from(row.querySelectorAll('div')).find((node) => (
        node.classList.contains('border') && node.querySelectorAll(':scope > button').length === 2
      ));
      const [decrease, increase] = quantityGroup?.querySelectorAll(':scope > button') || [];
      const decreaseLabel = root.dataset.eamdDecreaseLabel || translate('cart.decreaseQuantity');
      const increaseLabel = root.dataset.eamdIncreaseLabel || translate('cart.increaseQuantity');
      if (decrease && decreaseLabel) decrease.setAttribute('aria-label', productTitle ? `${decreaseLabel}: ${productTitle}` : decreaseLabel);
      if (increase && increaseLabel) increase.setAttribute('aria-label', productTitle ? `${increaseLabel}: ${productTitle}` : increaseLabel);
    });

    const checkout = Array.from(drawer.querySelectorAll('a[href]')).find((link) => {
      try {
        return new URL(link.href, window.location.origin).pathname.endsWith('/checkout');
      } catch (_) {
        return false;
      }
    });
    if (checkout) {
      const checkoutUrl = route(['checkout', 'checkoutUrl'], '/checkout');
      if (checkout.getAttribute('href') !== checkoutUrl) checkout.setAttribute('href', checkoutUrl);
      pintarPagos(checkout);
    }
  };

  /**
   * La fila de métodos de pago, bajo el botón de pagar (payment-logos-standard.md).
   *
   * Sale de lo que Liquid serializa en `settings.pagos`: `shop.enabled_payment_types` +
   * `payment_type_svg_tag` cuando Shopify declara métodos nativos, y si no (pasarela EXTERNA, como
   * Wompi aquí) los que el checkout real ofrece, verificados y con los SVG oficiales de la librería
   * (`snippets/eamd-pagos-verificados.liquid`, payment-logos-standard § 0.ter). Nunca una lista
   * escrita a mano: anunciar un método que la tienda no tiene es una promesa contractual. Sin
   * métodos verificados, no hay fila.
   */
  const pintarPagos = (checkout) => {
    /**
     * 🔴 El bundle compilado pinta SEIS logos dibujados a mano (Visa, Mastercard, PSE, Nequi,
     * Bancolombia, Efecty) tras el botón de pagar SIN MIRAR ninguna prop: `a.jsx(uy,{})` va
     * incondicional, así que `showPaymentMethods: false` no lo apaga (medido 2026-09-23). Esa
     * fila se oculta aquí; no se mueve ni se borra porque es DOM de React.
     */
    const fabrica = [...(checkout.parentElement?.children || [])]
      .find((n) => n !== checkout && !n.dataset.eamdPagos && [...n.querySelectorAll('svg[aria-label]')]
        .some((s) => /visa|master|pse|nequi|bancolombia|efecty|amex|paypal/i.test(s.getAttribute('aria-label'))));
    if (fabrica && !fabrica.dataset.eamdPagosFabrica) fabrica.dataset.eamdPagosFabrica = 'true';

    const settings = config();
    const pagos = settings?.mostrarPagos ? (settings.pagos || []).filter((p) => p?.svg) : [];
    const vieja = checkout.parentElement?.querySelector(':scope > [data-eamd-pagos]');
    if (!pagos.length) { vieja?.remove(); return; }
    if (vieja) return;
    const fila = document.createElement('div');
    fila.className = 'eamd-cart-pagos';
    fila.dataset.eamdPagos = 'true';
    fila.setAttribute('aria-label', translate('cart.paymentMethods'));
    // El SVG lo genera Shopify (`payment_type_svg_tag`) en el servidor, no el comprador.
    fila.innerHTML = pagos.map((p) => p.svg).join('');
    checkout.insertAdjacentElement('afterend', fila);
  };

  let cartSubtotal = null;
  let cartItemCount = null;
  let cartProductIds = new Set();
  let firstCartProductId = null;

  const refreshCart = async () => {
    try {
      const response = await fetch(route(['cartJson'], '/cart.js'), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      cartSubtotal = cart.total_price;
      cartItemCount = cart.item_count;
      cartProductIds = new Set((cart.items || []).map((line) => line.product_id));
      firstCartProductId = cart.items?.[0]?.product_id ?? null;
    } catch (_) {
      // Leave the previous known values rather than rendering a wrong state.
    }
  };

  /**
   * 🔴 Por que un flag SINCRONO y no un contador de generacion.
   *
   * El `MutationObserver` sobre `document.body` dispara `sync()` con CUALQUIER cambio del
   * DOM, y montar los recomendados ES un cambio del DOM: la primera pasada async (fetch a
   * `/recommendations/products.js`, unos cientos de ms) todavia no habia terminado cuando
   * ya llegaban decenas de `sync()` mas, cada uno viendo `dataset.eamdRecKey` con el valor
   * VIEJO (todavia no se escribe hasta que la promesa resuelve) y arrancando SU PROPIO fetch
   * en paralelo. Con un contador de generacion incrementado en cada intento, la generacion
   * mas nueva casi siempre llegaba ANTES de que cualquier fetch anterior resolviera —
   * inanicion: ninguna pasada ganaba nunca, y `eamdRecKey` se quedaba pegado en "vacio" para
   * siempre (medido).
   *
   * El fix: marcar la clave como "en curso" de forma SINCRONA, antes del primer `await`. La
   * siguiente reentrada del mismo `sync()` (aunque llegue en el mismo microtask) ve el
   * marcador puesto y se retira sin lanzar un segundo fetch.
   */
  const syncRecommendations = async (drawer, settings) => {
    const clave = cartItemCount === 0 ? 'vacio' : `lleno:${firstCartProductId}`;
    if (drawer.dataset.eamdRecKey === clave || drawer.dataset.eamdRecPending === clave) return;
    drawer.dataset.eamdRecPending = clave;

    try {
      const productos = cartItemCount === 0
        ? recomendadosDelPool(settings, cartProductIds)
        : await recomendadosDeLaApi(firstCartProductId, cartProductIds).then((api) => (
          api.length ? api : recomendadosDelPool(settings, cartProductIds)
        ));

      // El carrito pudo cambiar de nuevo mientras la API contestaba; una respuesta tardia no
      // pisa un estado mas reciente.
      if (drawer.dataset.eamdRecPending !== clave) return;

      const title = cartItemCount === 0
        ? (settings.recommendationsTitle || translate('cart.recommendationsTitle'))
        : translate('cart.completeYourPurchaseTitle');

      if (!productos.length) {
        removeRecommendations(drawer);
      } else {
        mountRecommendations(drawer, { title, productos });
      }
      drawer.dataset.eamdRecKey = clave;
    } catch (e) {
      // 🔴 Se escribe, no se traga: un cajon sin recomendados por un fallo silencioso es
      // indistinguible de uno sin catalogo suficiente, y son dos causas muy distintas.
      console.error('[eamd] no se pudieron montar los recomendados del carrito:', e);
    } finally {
      if (drawer.dataset.eamdRecPending === clave) delete drawer.dataset.eamdRecPending;
    }
  };

  const sync = () => {
    const settings = config();
    if (!settings) return;
    const drawer = findDrawer();
    if (!drawer) return;
    localizeCartChrome(drawer);
    if (cartItemCount === null) return;

    removeShippingBar(drawer);
    syncRecommendations(drawer, settings);
  };

  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      sync();
    });
  };

  const start = () => {
    refreshCart().then(sync);
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    ['cart:added', 'cart:refresh', 'cart:updated'].forEach((name) => {
      document.addEventListener(name, () => { refreshCart().then(sync); });
    });
  };

  document.addEventListener('shopify:section:load', () => {
    refreshCart().then(sync);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
